import { Controller } from "@hotwired/stimulus"
import toast from "../services/toast"

export default class extends Controller {
  static targets = [
    "tabsNav",
    "tabPanels", 
    "validationSummary",
    "validationList",
    "colorCount",
    "productCount"
  ]

  static values = {
    sizesUrl: { type: String, default: "/admin/corridas/:id/sizes" },
    categoryCodes: { type: Object, default: {} },
    cutCodes: { type: Object, default: {} },
    colorCodes: { type: Object, default: {} }
  }

  connect() {
    this.colorIndex = 0
    this.runIndex = 0
    this.manualCodeEdits = new Set() // track run indices edited manually
    this.initializeFirstTab()
    this.updateSummary()

    // Sync inherited hidden fields before form submission
    const form = this.element.closest('form')
    if (form) {
      form.addEventListener('submit', () => this.syncInheritedFields())
    }
  }

  // ==========================================================================
  // Tab Management
  // ==========================================================================

  initializeFirstTab() {
    // First tab is already in HTML, just need to track indices
    const firstTab = this.tabsNavTarget.querySelector(".variant-tab")
    const firstPanel = this.tabPanelsTarget.querySelector(".variant-tab-panel")
    
    if (firstTab && firstPanel) {
      firstTab.dataset.colorIndex = this.colorIndex
      firstPanel.dataset.colorIndex = this.colorIndex
      
      const firstRun = firstPanel.querySelector(".variant-run-card")
      if (firstRun) {
        firstRun.dataset.runIndex = this.runIndex
        this.updateRunFieldNames(firstRun, this.runIndex, this.colorIndex)
      }
      
      this.colorIndex++
      this.runIndex++
    }
  }

  addColor(event) {
    event.preventDefault()
    
    const newColorIndex = this.colorIndex++
    const newRunIndex = this.runIndex++
    
    // Create new tab
    const tab = this.createTab(newColorIndex)
    const addButton = this.tabsNavTarget.querySelector(".variant-tab-add")
    this.tabsNavTarget.insertBefore(tab, addButton)
    
    // Create new panel
    const panel = this.createPanel(newColorIndex, newRunIndex)
    this.tabPanelsTarget.appendChild(panel)
    
    // Switch to new tab
    this.switchToTab(newColorIndex)
    this.updateTabCloseButtons()
    this.updateSummary()
  }

  removeColor(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const tab = event.currentTarget.closest(".variant-tab")
    const colorIndex = tab.dataset.colorIndex
    const panel = this.tabPanelsTarget.querySelector(`[data-color-index="${colorIndex}"]`)
    
    // Don't remove if it's the last tab
    const allTabs = this.tabsNavTarget.querySelectorAll(".variant-tab")
    if (allTabs.length <= 1) return
    
    const wasActive = tab.classList.contains("is-active")
    
    // Remove tab and panel
    tab.remove()
    panel.remove()
    
    // If was active, activate first remaining tab
    if (wasActive) {
      const firstTab = this.tabsNavTarget.querySelector(".variant-tab")
      if (firstTab) {
        this.switchToTab(firstTab.dataset.colorIndex)
      }
    }
    
    this.updateTabCloseButtons()
    this.updateSummary()
  }

  switchTab(event) {
    const tab = event.currentTarget
    if (tab.classList.contains("variant-tab-add")) return
    
    this.switchToTab(tab.dataset.colorIndex)
  }

  switchToTab(colorIndex) {
    // Deactivate all tabs
    this.tabsNavTarget.querySelectorAll(".variant-tab").forEach(t => {
      t.classList.remove("is-active")
    })
    this.tabPanelsTarget.querySelectorAll(".variant-tab-panel").forEach(p => {
      p.classList.remove("is-active")
    })
    
    // Activate target
    const tab = this.tabsNavTarget.querySelector(`[data-color-index="${colorIndex}"]`)
    const panel = this.tabPanelsTarget.querySelector(`[data-color-index="${colorIndex}"]`)
    
    if (tab) tab.classList.add("is-active")
    if (panel) panel.classList.add("is-active")
  }

  createTab(colorIndex) {
    const tab = document.createElement("div")
    tab.className = "variant-tab"
    tab.dataset.colorIndex = colorIndex
    tab.dataset.action = "click->product-multi#switchTab"
    tab.innerHTML = `
      <span class="variant-tab-title" data-product-multi-target="tabTitle" data-color-index="${colorIndex}">Nuevo Color</span>
      <button type="button" class="variant-tab-close" data-action="click->product-multi#removeColor" title="Eliminar color">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    `
    return tab
  }

  createPanel(colorIndex, runIndex) {
    const panel = document.createElement("div")
    panel.className = "variant-tab-panel"
    panel.dataset.colorIndex = colorIndex
    panel.innerHTML = `
      <div class="variant-color-image">
        <div class="variant-color-image-title">Imagen del Color</div>
        <div class="variant-image-upload">
          <div class="variant-image-preview" data-product-multi-target="colorImagePreview" data-color-index="${colorIndex}">
            <div class="variant-image-placeholder">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              <span>Sin imagen</span>
            </div>
          </div>
          <div class="variant-image-actions">
            <input type="file" 
                   name="color_images[${colorIndex}]" 
                   accept="image/png,image/jpeg,image/jpg,image/webp"
                   class="variant-image-input"
                   data-action="change->product-multi#handleColorImageChange"
                   data-color-index="${colorIndex}">
            <button type="button" class="btn-upload-image" data-action="click->product-multi#triggerColorImageUpload" data-color-index="${colorIndex}">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              Subir imagen
            </button>
            <button type="button" class="btn-remove-image" style="display: none;" data-action="click->product-multi#removeColorImage" data-color-index="${colorIndex}">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
              Quitar
            </button>
          </div>
        </div>
      </div>

      <div class="variant-runs-title">
        <h4>Corridas</h4>
      </div>
      
      <div class="variant-runs-list" data-color-index="${colorIndex}">
        ${this.createRunCardHTML(runIndex, colorIndex, 1)}
      </div>

      <button type="button" class="btn-add-run" data-action="click->product-multi#addRun" data-color-index="${colorIndex}">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Agregar otra corrida
      </button>
    `
    return panel
  }

  updateTabCloseButtons() {
    const tabs = this.tabsNavTarget.querySelectorAll(".variant-tab")
    const closeButtons = this.tabsNavTarget.querySelectorAll(".variant-tab-close")
    
    closeButtons.forEach(btn => {
      btn.disabled = tabs.length <= 1
    })
  }

  // ==========================================================================
  // Run Management
  // ==========================================================================

  addRun(event) {
    event.preventDefault()
    
    const colorIndex = event.currentTarget.dataset.colorIndex
    const runsList = this.tabPanelsTarget.querySelector(`.variant-runs-list[data-color-index="${colorIndex}"]`)
    const currentRuns = runsList.querySelectorAll(".variant-run-card")
    
    const newRunIndex = this.runIndex++
    const runNumber = currentRuns.length + 1
    
    const runCard = document.createElement("div")
    runCard.className = "variant-run-card"
    runCard.dataset.runIndex = newRunIndex
    runCard.innerHTML = this.createRunCardHTML(newRunIndex, colorIndex, runNumber, true)
    
    runsList.appendChild(runCard.firstElementChild ? runCard.firstElementChild : runCard)
    
    // Re-parse to get actual element
    const addedCard = runsList.querySelector(`[data-run-index="${newRunIndex}"]`)
    if (!addedCard) {
      // If innerHTML didn't work as expected, manually set
      runsList.insertAdjacentHTML('beforeend', this.createRunCardHTML(newRunIndex, colorIndex, runNumber, true))
    }
    
    // Inherit values from the first run in this color group
    this.inheritFirstRunValues(colorIndex, newRunIndex)
    
    this.updateRunRemoveButtons(colorIndex)
    this.updateSummary()
  }

  removeRun(event) {
    event.preventDefault()
    
    const runCard = event.currentTarget.closest(".variant-run-card")
    const colorIndex = runCard.closest(".variant-runs-list").dataset.colorIndex
    const runsList = this.tabPanelsTarget.querySelector(`.variant-runs-list[data-color-index="${colorIndex}"]`)
    const allRuns = runsList.querySelectorAll(".variant-run-card")
    
    // Don't remove if it's the last run in the last tab
    const allTabs = this.tabsNavTarget.querySelectorAll(".variant-tab")
    if (allTabs.length <= 1 && allRuns.length <= 1) return
    
    runCard.remove()
    
    // Renumber remaining runs
    this.renumberRuns(colorIndex)
    this.updateRunRemoveButtons(colorIndex)
    this.updateSummary()
  }

  createRunCardHTML(runIndex, colorIndex, runNumber, isAdditionalRun = false) {
    // Additional runs (2nd, 3rd, etc.) inherit code/color from the first run
    // and don't show description fields (those are shared per color tab)
    const headerFields = isAdditionalRun ? `
            <div class="variant-run-field">
              <label class="variant-run-field-label">Corrida *</label>
              <select name="products[${runIndex}][size_run_id]" 
                      class="variant-run-field-input"
                      data-action="change->product-multi#loadSizes"
                      data-run-index="${runIndex}"
                      required>
                ${this.getSizeRunOptionsHTML()}
              </select>
              <span class="field-error" data-error-for="products[${runIndex}][size_run_id]"></span>
            </div>
            <div class="variant-run-field">
              <label class="variant-run-field-label">Ubicación</label>
              <select name="products[${runIndex}][location_id]" class="variant-run-field-input">
                ${this.getLocationOptionsHTML()}
              </select>
            </div>` : `
            <div class="variant-run-field">
              <label class="variant-run-field-label">Código *</label>
              <input type="text" 
                     name="products[${runIndex}][code]" 
                     class="variant-run-field-input" 
                     placeholder="Ej: ABC123"
                     data-action="input->product-multi#markCodeManual"
                     data-color-index="${colorIndex}"
                     data-run-index="${runIndex}"
                     required>
              <span class="field-error" data-error-for="products[${runIndex}][code]"></span>
            </div>
            <div class="variant-run-field">
              <label class="variant-run-field-label">Color *</label>
              <select name="products[${runIndex}][color_id]" class="variant-run-field-input" 
                      data-action="change->product-multi#generateCodeForRun change->product-multi#updateTabTitleFromColor"
                      data-color-index="${colorIndex}"
                      required>
                ${this.getColorOptionsHTML()}
              </select>
              <span class="field-error" data-error-for="products[${runIndex}][color_id]"></span>
            </div>
            <div class="variant-run-field">
              <label class="variant-run-field-label">Corrida *</label>
              <select name="products[${runIndex}][size_run_id]" 
                      class="variant-run-field-input"
                      data-action="change->product-multi#loadSizes"
                      data-run-index="${runIndex}"
                      required>
                ${this.getSizeRunOptionsHTML()}
              </select>
              <span class="field-error" data-error-for="products[${runIndex}][size_run_id]"></span>
            </div>
            <div class="variant-run-field">
              <label class="variant-run-field-label">Ubicación</label>
              <select name="products[${runIndex}][location_id]" class="variant-run-field-input">
                ${this.getLocationOptionsHTML()}
              </select>
            </div>`

    // Hidden fields for inherited values on additional runs
    const hiddenFields = isAdditionalRun ? `
          <input type="hidden" name="products[${runIndex}][code]" value="" data-inherit="code" data-color-index="${colorIndex}">
          <input type="hidden" name="products[${runIndex}][color_id]" value="" data-inherit="color_id" data-color-index="${colorIndex}">
          <input type="hidden" name="products[${runIndex}][supplier_description]" value="" data-inherit="supplier_description" data-color-index="${colorIndex}">
          <input type="hidden" name="products[${runIndex}][web_description]" value="" data-inherit="web_description" data-color-index="${colorIndex}">` : ''

    const descriptionsHTML = isAdditionalRun ? '' : `
          <div class="variant-run-descriptions">
            <div class="variant-run-field variant-run-field-wide">
              <label class="variant-run-field-label">Descripción Proveedor</label>
              <input type="text" 
                     name="products[${runIndex}][supplier_description]" 
                     class="variant-run-field-input" 
                     placeholder="Descripción del proveedor">
            </div>
            <div class="variant-run-field variant-run-field-wide">
              <label class="variant-run-field-label">Descripción Web</label>
              <input type="text" 
                     name="products[${runIndex}][web_description]" 
                     class="variant-run-field-input" 
                     placeholder="Descripción para la web">
            </div>
          </div>`

    return `
      <div class="variant-run-card" data-run-index="${runIndex}">
        <div class="variant-run-header">
          <div class="variant-run-number">${runNumber}</div>
          <div class="variant-run-fields${isAdditionalRun ? ' variant-run-fields--compact' : ''}">
            ${headerFields}
          </div>
          <div class="variant-run-actions">
            <button type="button" class="btn-remove-run" data-action="click->product-multi#removeRun" title="Eliminar corrida">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="variant-run-pricing" data-controller="product-profit">
            <div class="variant-run-pricing-header">
              <h5>Precios y utilidad</h5>
            </div>
            <div class="product-pricing-rows">
              <div class="product-pricing-row-header">
                <span></span>
                <span>Costo</span>
                <span>Precio</span>
                <span>% Utilidad</span>
              </div>
              <div class="product-pricing-row">
                <span class="product-pricing-row__label">
                  <input type="radio" name="products[${runIndex}][active_price]" value="1" checked id="active_price_${runIndex}_1">
                  <label for="active_price_${runIndex}_1" class="product-pricing-row__radio-label">1</label>
                </span>
                <div class="form-group">
                  <div class="input-with-prefix-sm">
                    <span class="input-prefix">$</span>
                    <input type="number" name="products[${runIndex}][cost_1]" step="0.01" min="0"
                           class="variant-run-field-input" placeholder="0.00"
                           data-product-profit-target="cost1" data-row="1"
                           data-action="input->product-profit#updateFromCostRow change->product-profit#updateFromCostRow">
                  </div>
                </div>
                <div class="form-group">
                  <div class="input-with-prefix-sm">
                    <span class="input-prefix">$</span>
                    <input type="number" name="products[${runIndex}][price]" step="0.01"
                           class="variant-run-field-input" placeholder="0.00"
                           data-product-profit-target="price1" data-row="1"
                           data-action="input->product-profit#updateFromPriceRow change->product-profit#updateFromPriceRow">
                  </div>
                </div>
                <div class="form-group">
                  <div class="input-with-suffix-sm">
                    <input type="number" step="0.01" min="0"
                           class="variant-run-field-input" placeholder="0.00"
                           data-product-profit-target="margin1" data-row="1"
                           data-action="input->product-profit#updateFromMarginRow change->product-profit#updateFromMarginRow">
                    <span class="input-suffix">%</span>
                  </div>
                </div>
              </div>
              <div class="product-pricing-row">
                <span class="product-pricing-row__label">
                  <input type="radio" name="products[${runIndex}][active_price]" value="2" id="active_price_${runIndex}_2">
                  <label for="active_price_${runIndex}_2" class="product-pricing-row__radio-label">2</label>
                </span>
                <div class="form-group">
                  <div class="input-with-prefix-sm">
                    <span class="input-prefix">$</span>
                    <input type="number" name="products[${runIndex}][cost_2]" step="0.01" min="0"
                           class="variant-run-field-input" placeholder="0.00"
                           data-product-profit-target="cost2" data-row="2"
                           data-action="input->product-profit#updateFromCostRow change->product-profit#updateFromCostRow">
                  </div>
                </div>
                <div class="form-group">
                  <div class="input-with-prefix-sm">
                    <span class="input-prefix">$</span>
                    <input type="number" name="products[${runIndex}][price_2]" step="0.01"
                           class="variant-run-field-input" placeholder="0.00"
                           data-product-profit-target="price2" data-row="2"
                           data-action="input->product-profit#updateFromPriceRow change->product-profit#updateFromPriceRow">
                  </div>
                </div>
                <div class="form-group">
                  <div class="input-with-suffix-sm">
                    <input type="number" step="0.01" min="0"
                           class="variant-run-field-input" placeholder="0.00"
                           data-product-profit-target="margin2" data-row="2"
                           data-action="input->product-profit#updateFromMarginRow change->product-profit#updateFromMarginRow">
                    <span class="input-suffix">%</span>
                  </div>
                </div>
              </div>
              <div class="product-pricing-row">
                <span class="product-pricing-row__label">
                  <input type="radio" name="products[${runIndex}][active_price]" value="3" id="active_price_${runIndex}_3">
                  <label for="active_price_${runIndex}_3" class="product-pricing-row__radio-label">3</label>
                </span>
                <div class="form-group">
                  <div class="input-with-prefix-sm">
                    <span class="input-prefix">$</span>
                    <input type="number" name="products[${runIndex}][cost_3]" step="0.01" min="0"
                           class="variant-run-field-input" placeholder="0.00"
                           data-product-profit-target="cost3" data-row="3"
                           data-action="input->product-profit#updateFromCostRow change->product-profit#updateFromCostRow">
                  </div>
                </div>
                <div class="form-group">
                  <div class="input-with-prefix-sm">
                    <span class="input-prefix">$</span>
                    <input type="number" name="products[${runIndex}][price_3]" step="0.01"
                           class="variant-run-field-input" placeholder="0.00"
                           data-product-profit-target="price3" data-row="3"
                           data-action="input->product-profit#updateFromPriceRow change->product-profit#updateFromPriceRow">
                  </div>
                </div>
                <div class="form-group">
                  <div class="input-with-suffix-sm">
                    <input type="number" step="0.01" min="0"
                           class="variant-run-field-input" placeholder="0.00"
                           data-product-profit-target="margin3" data-row="3"
                           data-action="input->product-profit#updateFromMarginRow change->product-profit#updateFromMarginRow">
                    <span class="input-suffix">%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          ${descriptionsHTML}
          ${hiddenFields}
          <input type="hidden" name="products[${runIndex}][color_group]" value="${colorIndex}">

        <div class="variant-run-image-override">
          <label class="variant-run-image-toggle">
            <input type="checkbox" 
                   name="products[${runIndex}][use_own_image]" 
                   value="1"
                   data-action="change->product-multi#toggleRunImage"
                   data-run-index="${runIndex}">
            <span>Usar imagen diferente para esta corrida</span>
          </label>
          <div class="variant-run-image-upload" data-run-index="${runIndex}">
            <div class="variant-run-image-preview">
              <div class="variant-run-image-placeholder">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
              </div>
            </div>
            <input type="file" 
                   name="products[${runIndex}][image]" 
                   accept="image/png,image/jpeg,image/jpg,image/webp"
                   class="variant-image-input"
                   data-action="change->product-multi#handleRunImageChange"
                   data-run-index="${runIndex}">
            <button type="button" class="btn-upload-image" data-action="click->product-multi#triggerRunImageUpload" data-run-index="${runIndex}">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              Subir
            </button>
          </div>
        </div>

        <div class="variant-run-body">
          <div class="variant-run-inventory" data-run-index="${runIndex}">
            <div class="variant-run-inventory-title">Inventario Inicial</div>
            <div class="variant-run-inventory-empty">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
              </svg>
              <p>Selecciona una corrida para ver las tallas</p>
            </div>
            <div class="table-container" style="display: none; max-width: 650px;">
              <table class="inventory-table-custom">
                <thead>
                  <tr>
                    <th>TALLA</th>
                    <th>
                      <div style="display: flex; align-items: center; justify-content: center; gap: 0.5rem;">
                        <button type="button" class="btn-increment" data-action="click->product-multi#decrementColumn" data-run-index="${runIndex}" data-column="stock">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14"/></svg>
                        </button>
                        <span>STOCK</span>
                        <button type="button" class="btn-increment" data-action="click->product-multi#incrementColumn" data-run-index="${runIndex}" data-column="stock">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5v14"/></svg>
                        </button>
                      </div>
                    </th>
                    <th>
                      <div style="display: flex; align-items: center; justify-content: center; gap: 0.5rem;">
                        <button type="button" class="btn-increment" data-action="click->product-multi#decrementColumn" data-run-index="${runIndex}" data-column="minimum">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14"/></svg>
                        </button>
                        <span>STOCK MÍN.</span>
                        <button type="button" class="btn-increment" data-action="click->product-multi#incrementColumn" data-run-index="${runIndex}" data-column="minimum">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5v14"/></svg>
                        </button>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody data-sizes-table data-run-index="${runIndex}"></tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    `
  }

  updateRunFieldNames(runCard, runIndex, colorIndex) {
    // Update all input names with correct indices
    runCard.querySelectorAll("input, select").forEach(input => {
      const name = input.name
      if (name) {
        input.name = name.replace(/products\[\d+\]/, `products[${runIndex}]`)
      }
      // Update data-run-index on elements that have it
      if (input.dataset.runIndex !== undefined) {
        input.dataset.runIndex = runIndex
      }
    })
    
    // Update data-run-index on the run card itself
    runCard.dataset.runIndex = runIndex
    
    // Update data-run-index on inventory container
    const inventoryContainer = runCard.querySelector('.variant-run-inventory')
    if (inventoryContainer) {
      inventoryContainer.dataset.runIndex = runIndex
    }
    
    // Update data-run-index on sizes table tbody
    const sizesTable = runCard.querySelector('[data-sizes-table]')
    if (sizesTable) {
      sizesTable.dataset.runIndex = runIndex
    }
    
    // Update data-run-index on all buttons with it
    runCard.querySelectorAll('[data-run-index]').forEach(el => {
      el.dataset.runIndex = runIndex
    })
    
    // Update color_group hidden input
    const colorGroupInput = runCard.querySelector('input[name*="[color_group]"]')
    if (colorGroupInput) {
      colorGroupInput.value = colorIndex
    }
    
    // Update data-color-index on inputs that have it
    runCard.querySelectorAll('[data-color-index]').forEach(el => {
      el.dataset.colorIndex = colorIndex
    })
  }

  renumberRuns(colorIndex) {
    const runsList = this.tabPanelsTarget.querySelector(`.variant-runs-list[data-color-index="${colorIndex}"]`)
    const runs = runsList.querySelectorAll(".variant-run-card")
    
    runs.forEach((run, index) => {
      const numberEl = run.querySelector(".variant-run-number")
      if (numberEl) numberEl.textContent = index + 1
    })
  }

  updateRunRemoveButtons(colorIndex) {
    const allTabs = this.tabsNavTarget.querySelectorAll(".variant-tab")
    const runsList = this.tabPanelsTarget.querySelector(`.variant-runs-list[data-color-index="${colorIndex}"]`)
    const runs = runsList.querySelectorAll(".variant-run-card")
    const removeButtons = runsList.querySelectorAll(".btn-remove-run")
    
    // Disable remove button if it's the only run in the only tab
    const shouldDisable = allTabs.length <= 1 && runs.length <= 1
    
    removeButtons.forEach(btn => {
      btn.disabled = shouldDisable
    })
  }

  // ==========================================================================
  // Tab Title Updates
  // ==========================================================================

  updateTabTitleFromColor(event) {
    const select = event.currentTarget
    const colorIndex = select.dataset.colorIndex || select.closest(".variant-runs-list")?.dataset.colorIndex
    if (!colorIndex && colorIndex !== "0") return

    const tabTitle = this.tabsNavTarget.querySelector(`.variant-tab-title[data-color-index="${colorIndex}"]`)
    if (tabTitle) {
      const selectedOption = select.options[select.selectedIndex]
      tabTitle.textContent = (selectedOption && selectedOption.value) ? selectedOption.text : "Nuevo Color"
    }
  }

  // ==========================================================================
  // ==========================================================================
  // Inherit Values for Additional Runs
  // ==========================================================================

  inheritFirstRunValues(colorIndex, newRunIndex) {
    const runsList = this.tabPanelsTarget.querySelector(`.variant-runs-list[data-color-index="${colorIndex}"]`)
    if (!runsList) return

    const firstRun = runsList.querySelector('.variant-run-card')
    if (!firstRun) return

    // Get values from first run's visible fields
    const codeInput = firstRun.querySelector(`input[name^="products["][name$="[code]"]:not([type="hidden"])`)
    const colorSelect = firstRun.querySelector(`select[name^="products["][name$="[color_id]"]`)
    const descProvInput = firstRun.querySelector(`input[name^="products["][name$="[supplier_description]"]:not([type="hidden"])`)
    const descWebInput = firstRun.querySelector(`input[name^="products["][name$="[web_description]"]:not([type="hidden"])`)

    // Set hidden inherited fields on the new run
    const newRun = runsList.querySelector(`.variant-run-card[data-run-index="${newRunIndex}"]`)
    if (!newRun) return

    const setHidden = (field, value) => {
      const el = newRun.querySelector(`input[data-inherit="${field}"][data-color-index="${colorIndex}"]`)
      if (el) el.value = value || ''
    }

    setHidden('code', codeInput?.value)
    setHidden('color_id', colorSelect?.value)
    setHidden('supplier_description', descProvInput?.value)
    setHidden('web_description', descWebInput?.value)
  }

  syncInheritedFields() {
    // Before form submission, sync all inherited hidden fields from their first-run source
    const colorPanels = this.tabPanelsTarget.querySelectorAll('.variant-runs-list')
    colorPanels.forEach(runsList => {
      const colorIndex = runsList.dataset.colorIndex
      const runs = runsList.querySelectorAll('.variant-run-card')
      if (runs.length < 2) return

      const firstRun = runs[0]
      const codeVal = firstRun.querySelector(`input[name^="products["][name$="[code]"]:not([type="hidden"])`)?.value || ''
      const colorVal = firstRun.querySelector(`select[name^="products["][name$="[color_id]"]`)?.value || ''
      const descProvVal = firstRun.querySelector(`input[name^="products["][name$="[supplier_description]"]:not([type="hidden"])`)?.value || ''
      const descWebVal = firstRun.querySelector(`input[name^="products["][name$="[web_description]"]:not([type="hidden"])`)?.value || ''

      for (let i = 1; i < runs.length; i++) {
        const run = runs[i]
        const setVal = (field, val) => {
          const el = run.querySelector(`input[data-inherit="${field}"]`)
          if (el) el.value = val
        }
        setVal('code', codeVal)
        setVal('color_id', colorVal)
        setVal('supplier_description', descProvVal)
        setVal('web_description', descWebVal)
      }
    })
  }

  // ==========================================================================
  // Auto Code Generation
  // ==========================================================================

  markCodeManual(event) {
    const runIndex = event.currentTarget.dataset.runIndex
    if (runIndex !== undefined) {
      this.manualCodeEdits.add(runIndex)
    }
  }

  generateCodes() {
    // Build the shared prefix from category, brand, model, cut
    const prefix = this._buildCodePrefix()
    
    // Update all code inputs that haven't been manually edited
    const allRuns = this.tabPanelsTarget.querySelectorAll(".variant-run-card")
    allRuns.forEach(run => {
      const runIndex = run.dataset.runIndex
      if (this.manualCodeEdits.has(runIndex)) return
      
      const codeInput = run.querySelector(`input[name="products[${runIndex}][code]"]`)
      const colorSelect = run.querySelector(`select[name="products[${runIndex}][color_id]"]`)
      if (!codeInput) return
      
      const colorPart = this._getColorCode(colorSelect)
      const parts = [prefix, colorPart].filter(p => p)
      codeInput.value = parts.join(" ")
    })
  }

  generateCodeForRun(event) {
    const select = event.currentTarget
    const runCard = select.closest(".variant-run-card")
    if (!runCard) return
    
    const runIndex = runCard.dataset.runIndex
    if (this.manualCodeEdits.has(runIndex)) return
    
    const codeInput = runCard.querySelector(`input[name="products[${runIndex}][code]"]`)
    if (!codeInput) return
    
    const prefix = this._buildCodePrefix()
    const colorPart = this._getColorCode(select)
    const parts = [prefix, colorPart].filter(p => p)
    codeInput.value = parts.join(" ")
  }

  _buildCodePrefix() {
    const parts = []
    const form = this.element
    
    // Category
    const catSelect = form.querySelector('select[name="shared[category_id]"]')
    if (catSelect && catSelect.value) {
      parts.push(this.categoryCodesValue[catSelect.value] || catSelect.options[catSelect.selectedIndex]?.text?.trim() || "")
    }
    
    // Brand
    const brandSelect = form.querySelector('select[name="shared[brand_id]"]')
    if (brandSelect && brandSelect.value) {
      parts.push(brandSelect.options[brandSelect.selectedIndex]?.text?.trim() || "")
    }
    
    // Model
    const modelInput = form.querySelector('input[name="shared[model_name]"]')
    if (modelInput && modelInput.value.trim()) {
      parts.push(modelInput.value.trim())
    }
    
    // Cut
    const cutSelect = form.querySelector('select[name="shared[cut_id]"]')
    if (cutSelect && cutSelect.value) {
      parts.push(this.cutCodesValue[cutSelect.value] || cutSelect.options[cutSelect.selectedIndex]?.text?.trim() || "")
    }
    
    return parts.filter(p => p).join(" ")
  }

  _getColorCode(colorSelect) {
    if (!colorSelect || !colorSelect.value) return ""
    return this.colorCodesValue[colorSelect.value] || colorSelect.options[colorSelect.selectedIndex]?.text?.trim() || ""
  }

  // ==========================================================================
  // Image Handling
  // ==========================================================================

  triggerColorImageUpload(event) {
    const colorIndex = event.currentTarget.dataset.colorIndex
    const input = this.element.querySelector(`input[name="color_images[${colorIndex}]"]`)
    if (input) input.click()
  }

  handleColorImageChange(event) {
    const input = event.currentTarget
    const colorIndex = input.dataset.colorIndex
    const file = input.files[0]
    
    if (!file) return
    
    // Validate file
    if (!this.validateImageFile(file)) {
      input.value = ""
      return
    }
    
    // Show preview
    const preview = this.element.querySelector(`.variant-image-preview[data-color-index="${colorIndex}"]`)
    const removeBtn = input.parentElement.querySelector(".btn-remove-image")
    
    const reader = new FileReader()
    reader.onload = (e) => {
      preview.innerHTML = `<img src="${e.target.result}" alt="Preview">`
      preview.classList.add("has-image")
      if (removeBtn) removeBtn.style.display = "inline-flex"
    }
    reader.readAsDataURL(file)
  }

  removeColorImage(event) {
    const colorIndex = event.currentTarget.dataset.colorIndex
    const input = this.element.querySelector(`input[name="color_images[${colorIndex}]"]`)
    const preview = this.element.querySelector(`.variant-image-preview[data-color-index="${colorIndex}"]`)
    const removeBtn = event.currentTarget
    
    if (input) input.value = ""
    
    preview.innerHTML = `
      <div class="variant-image-placeholder">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          <circle cx="8.5" cy="8.5" r="1.5"/>
          <polyline points="21 15 16 10 5 21"/>
        </svg>
        <span>Sin imagen</span>
      </div>
    `
    preview.classList.remove("has-image")
    removeBtn.style.display = "none"
  }

  toggleRunImage(event) {
    const checkbox = event.currentTarget
    const runIndex = checkbox.dataset.runIndex
    const uploadContainer = this.element.querySelector(`.variant-run-image-upload[data-run-index="${runIndex}"]`)
    
    if (uploadContainer) {
      uploadContainer.classList.toggle("is-visible", checkbox.checked)
    }
  }

  triggerRunImageUpload(event) {
    const runIndex = event.currentTarget.dataset.runIndex
    const input = this.element.querySelector(`input[name="products[${runIndex}][image]"]`)
    if (input) input.click()
  }

  handleRunImageChange(event) {
    const input = event.currentTarget
    const runIndex = input.dataset.runIndex
    const file = input.files[0]
    
    if (!file) return
    
    if (!this.validateImageFile(file)) {
      input.value = ""
      return
    }
    
    const previewContainer = input.closest(".variant-run-image-upload")
    const preview = previewContainer.querySelector(".variant-run-image-preview")
    
    const reader = new FileReader()
    reader.onload = (e) => {
      preview.innerHTML = `<img src="${e.target.result}" alt="Preview">`
      preview.classList.add("has-image")
    }
    reader.readAsDataURL(file)
  }

  validateImageFile(file) {
    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"]
    const maxSize = 2 * 1024 * 1024 // 2MB
    
    if (!validTypes.includes(file.type)) {
      toast.warning("Por favor selecciona una imagen válida (PNG, JPG o WEBP)")
      return false
    }
    
    if (file.size > maxSize) {
      toast.warning("La imagen no debe superar los 2MB")
      return false
    }
    
    return true
  }

  // ==========================================================================
  // Inventory Size Loading
  // ==========================================================================

  async loadSizes(event) {
    const select = event.currentTarget
    const runIndex = select.dataset.runIndex
    const sizeRunId = select.value
    
    const inventoryContainer = this.element.querySelector(`.variant-run-inventory[data-run-index="${runIndex}"]`)
    
    if (!inventoryContainer) {
      console.error("Inventory container not found for runIndex:", runIndex)
      return
    }
    
    const emptyState = inventoryContainer.querySelector(".variant-run-inventory-empty")
    const tableContainer = inventoryContainer.querySelector(".table-container")
    const tbody = inventoryContainer.querySelector("[data-sizes-table]")
    
    if (!sizeRunId) {
      emptyState.style.display = "block"
      tableContainer.style.display = "none"
      tbody.innerHTML = ""
      return
    }
    
    try {
      const url = this.sizesUrlValue.replace(":id", sizeRunId)
      const response = await fetch(url)
      const data = await response.json()
      
      this.renderSizesTable(tbody, data.sizes, runIndex)
      
      emptyState.style.display = "none"
      tableContainer.style.display = "block"
    } catch (error) {
      console.error("Error loading sizes:", error)
    }
  }

  renderSizesTable(tbody, sizes, runIndex) {
    const rows = sizes.map((size, index) => `
      <tr>
        <td>${size}</td>
        <td>
          <div class="input-with-controls">
            <button type="button" class="btn-increment" data-action="click->product-multi#decrementValue" data-run-index="${runIndex}" data-size-index="${index}" data-field="quantity">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14"/></svg>
            </button>
            <input type="number" 
                   name="products[${runIndex}][inventories_attributes][${index}][quantity]" 
                   value="0" 
                   min="0"
                   class="form-control form-control-sm">
            <button type="button" class="btn-increment" data-action="click->product-multi#incrementValue" data-run-index="${runIndex}" data-size-index="${index}" data-field="quantity">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5v14"/></svg>
            </button>
            <input type="hidden" name="products[${runIndex}][inventories_attributes][${index}][size]" value="${size}">
          </div>
        </td>
        <td>
          <div class="input-with-controls">
            <button type="button" class="btn-increment" data-action="click->product-multi#decrementValue" data-run-index="${runIndex}" data-size-index="${index}" data-field="minimum_stock">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14"/></svg>
            </button>
            <input type="number" 
                   name="products[${runIndex}][inventories_attributes][${index}][minimum_stock]" 
                   value="0" 
                   min="0"
                   class="form-control form-control-sm">
            <button type="button" class="btn-increment" data-action="click->product-multi#incrementValue" data-run-index="${runIndex}" data-size-index="${index}" data-field="minimum_stock">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5v14"/></svg>
            </button>
          </div>
        </td>
      </tr>
    `).join("")
    
    tbody.innerHTML = rows
  }

  incrementValue(event) {
    const button = event.currentTarget
    const runIndex = button.dataset.runIndex
    const sizeIndex = button.dataset.sizeIndex
    const field = button.dataset.field
    
    const input = this.element.querySelector(`input[name="products[${runIndex}][inventories_attributes][${sizeIndex}][${field}]"]`)
    if (input) {
      input.value = (parseInt(input.value) || 0) + 1
    }
  }

  decrementValue(event) {
    const button = event.currentTarget
    const runIndex = button.dataset.runIndex
    const sizeIndex = button.dataset.sizeIndex
    const field = button.dataset.field
    
    const input = this.element.querySelector(`input[name="products[${runIndex}][inventories_attributes][${sizeIndex}][${field}]"]`)
    if (input) {
      input.value = Math.max(0, (parseInt(input.value) || 0) - 1)
    }
  }

  incrementColumn(event) {
    const button = event.currentTarget
    const runIndex = button.dataset.runIndex
    const column = button.dataset.column
    const field = column === "stock" ? "quantity" : "minimum_stock"
    
    const inputs = this.element.querySelectorAll(`input[name*="products[${runIndex}][inventories_attributes]"][name*="[${field}]"]`)
    inputs.forEach(input => {
      input.value = (parseInt(input.value) || 0) + 1
    })
  }

  decrementColumn(event) {
    const button = event.currentTarget
    const runIndex = button.dataset.runIndex
    const column = button.dataset.column
    const field = column === "stock" ? "quantity" : "minimum_stock"
    
    const inputs = this.element.querySelectorAll(`input[name*="products[${runIndex}][inventories_attributes]"][name*="[${field}]"]`)
    inputs.forEach(input => {
      input.value = Math.max(0, (parseInt(input.value) || 0) - 1)
    })
  }

  // ==========================================================================
  // Options HTML (populated from data attributes or server)
  // ==========================================================================

  getColorOptionsHTML() {
    const colorsData = this.element.dataset.colors
    if (!colorsData) return '<option value="">Seleccionar...</option>'
    
    try {
      const colors = JSON.parse(colorsData)
      const options = colors.map(c => `<option value="${c.id}">${c.name}</option>`)
      return `<option value="">Seleccionar...</option>${options.join("")}`
    } catch {
      return '<option value="">Seleccionar...</option>'
    }
  }

  getSizeRunOptionsHTML() {
    const sizeRunsData = this.element.dataset.sizeRuns
    if (!sizeRunsData) return '<option value="">Seleccionar...</option>'
    
    try {
      const sizeRuns = JSON.parse(sizeRunsData)
      const options = sizeRuns.map(sr => `<option value="${sr.id}">${sr.name}</option>`)
      return `<option value="">Seleccionar...</option>${options.join("")}`
    } catch {
      return '<option value="">Seleccionar...</option>'
    }
  }

  getLocationOptionsHTML() {
    const locationsData = this.element.dataset.locations
    if (!locationsData) return '<option value="">Sin ubicación</option>'
    
    try {
      const locations = JSON.parse(locationsData)
      const options = locations.map(l => `<option value="${l.id}">${l.name}</option>`)
      return `<option value="">Sin ubicación</option>${options.join("")}`
    } catch {
      return '<option value="">Sin ubicación</option>'
    }
  }

  // ==========================================================================
  // Summary
  // ==========================================================================

  updateSummary() {
    const tabs = this.tabsNavTarget.querySelectorAll(".variant-tab")
    const runs = this.element.querySelectorAll(".variant-run-card")
    
    if (this.hasColorCountTarget) {
      this.colorCountTarget.textContent = tabs.length
    }
    if (this.hasProductCountTarget) {
      this.productCountTarget.textContent = runs.length
    }
  }

  // ==========================================================================
  // Validation
  // ==========================================================================

  validateForm(event) {
    const errors = []
    
    // Clear previous errors
    this.element.querySelectorAll(".field-error").forEach(el => {
      el.textContent = ""
    })
    this.element.querySelectorAll(".has-error").forEach(el => {
      el.classList.remove("has-error")
    })
    
    // Validate each run
    const runs = this.element.querySelectorAll(".variant-run-card")
    runs.forEach((run, index) => {
      const runIndex = run.dataset.runIndex
      
      // Code validation (solo requerido, puede repetirse)
      const codeInput = run.querySelector(`input[name="products[${runIndex}][code]"]`)
      if (codeInput) {
        const code = codeInput.value.trim()
        if (!code) {
          this.setFieldError(codeInput, "El código es requerido")
          errors.push(`Producto ${index + 1}: Código requerido`)
        }
      }
      
      // Color validation
      const colorSelect = run.querySelector(`select[name="products[${runIndex}][color_id]"]`)
      if (colorSelect && !colorSelect.value) {
        this.setFieldError(colorSelect, "Selecciona un color")
        errors.push(`Producto ${index + 1}: Color requerido`)
      }
      
      // Size run validation
      const sizeRunSelect = run.querySelector(`select[name="products[${runIndex}][size_run_id]"]`)
      if (sizeRunSelect && !sizeRunSelect.value) {
        this.setFieldError(sizeRunSelect, "Selecciona una corrida")
        errors.push(`Producto ${index + 1}: Corrida requerida`)
      }
    })
    
    // Show validation summary if errors
    if (errors.length > 0) {
      event.preventDefault()
      this.showValidationSummary(errors)
      
      // Scroll to first error
      const firstError = this.element.querySelector(".has-error")
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" })
      }
      
      return false
    }
    
    this.hideValidationSummary()
    return true
  }

  setFieldError(input, message) {
    input.classList.add("has-error")
    const errorSpan = input.parentElement.querySelector(".field-error")
    if (errorSpan) {
      errorSpan.textContent = message
    }
    
    // Mark card as having error
    const card = input.closest(".variant-run-card")
    if (card) card.classList.add("has-error")
  }

  showValidationSummary(errors) {
    if (this.hasValidationSummaryTarget) {
      this.validationSummaryTarget.classList.add("is-visible")
      if (this.hasValidationListTarget) {
        this.validationListTarget.innerHTML = errors.map(e => `<li>${e}</li>`).join("")
      }
    }
  }

  hideValidationSummary() {
    if (this.hasValidationSummaryTarget) {
      this.validationSummaryTarget.classList.remove("is-visible")
    }
  }
}
